/**
 * Offline school demo — localStorage fixtures, no auth / API.
 * Mirrors seeded Sala Caracas (schoolTestFixture) owner experience:
 * door check-in, fee approvals, multi-instructor schedule.
 * Owner: /demo/school?demo=true
 *
 * Guest event demo also surfaces DEMO_HOST_SCHOOL_PROMO (lead-gen band).
 */

import { CLASS_DEMO_PATH } from "./demoClassExperience";

const STORAGE_KEY = "ln_school_demo_state_v1";

export const SCHOOL_DEMO_PATH = "/demo/school?demo=true";
export const SCHOOL_DEMO_ID = "demo-school-marta";

function nextWeekdayAt(weekday, hour, minute) {
  // weekday: 0 Sun … 6 Sat
  const d = new Date();
  const day = d.getDay();
  let delta = (weekday - day + 7) % 7;
  d.setDate(d.getDate() + delta);
  d.setHours(hour, minute, 0, 0);
  if (d.getTime() < Date.now() - 30 * 60 * 1000) {
    d.setDate(d.getDate() + 7);
  }
  return d.toISOString();
}

function parseHm(hm) {
  const [h, m] = String(hm).split(":").map(Number);
  return { hour: h || 0, minute: m || 0 };
}

/** Static catalog — same shape as supabase schoolTestFixture. */
export const DEMO_SCHOOL = {
  id: SCHOOL_DEMO_ID,
  name: "Sala Caracas Dance Academy",
  slug: "sala-caracas-dance-academy-berlin",
  description: "Cuban & LA Salsa in Prenzlauer Berg. Beginner-friendly Fridays.",
  city: "Berlin",
  currency: "EUR",
  commerce_enabled: true,
  instagram: "https://instagram.com/salacaracas",
  whatsapp_url: null,
};

/** Shape matches API `event.host_school` — used on guest demo event / check-in. */
export const DEMO_HOST_SCHOOL_PROMO = {
  kind: "internal",
  name: "Sala Caracas",
  href: CLASS_DEMO_PATH,
  city: DEMO_SCHOOL.city,
  blurb: "Cuban and LA salsa in Prenzlauer Berg. Beginner classes every Friday.",
};

export const DEMO_SCHOOL_VENUES = [
  {
    id: "demo-school-venue-main",
    name: "Sala Caracas Main Studio",
    address_or_link: "Schönhauser Allee 36, 10435 Berlin",
  },
  {
    id: "demo-school-venue-rooftop",
    name: "Sala Caracas Rooftop Studio",
    address_or_link: "Prenzlauer Berg, Berlin",
  },
];

export const DEMO_SCHOOL_TEAM = [
  { id: "demo-member-marta", name: "Marta", role: "ADMIN" },
  { id: "demo-member-marco", name: "Marco", role: "ADMIN" },
  { id: "demo-member-lina", name: "Lina", role: "INSTRUCTOR" },
  { id: "demo-member-tomas", name: "Tomas", role: "INSTRUCTOR" },
];

export const DEMO_SCHOOL_PRODUCTS = [
  { id: "demo-school-product-dropin", name: "Single Class Drop-in", type: "DROPIN", price: 16, credits: null },
  { id: "demo-school-product-pack-5", name: "5-Class Pack", type: "PACK", price: 70, credits: 5 },
  { id: "demo-school-product-pack-10", name: "10-Class Pack", type: "PACK", price: 125, credits: 10 },
  { id: "demo-school-product-monthly", name: "Monthly Unlimited", type: "MEMBERSHIP", price: 89, credits: null },
];

const CLASS_SPECS = [
  {
    id: "demo-school-class-foundations",
    name: "Salsa Foundations",
    description: "Beginner-friendly Cuban and LA salsa fundamentals.",
    style_tag: "Salsa",
    level_tag: "Beginner",
    instructors: ["Lina"],
    venue_id: "demo-school-venue-main",
    schedules: [
      { day: "MON", time: "18:30", duration_minutes: 60 },
      { day: "WED", time: "18:30", duration_minutes: 60 },
    ],
  },
  {
    id: "demo-school-class-cuban",
    name: "Cuban Salsa Improvers",
    description: "Rueda, musicality, and confident social dancing.",
    style_tag: "Cuban Salsa",
    level_tag: "Improver",
    instructors: ["Tomas"],
    venue_id: "demo-school-venue-main",
    schedules: [{ day: "TUE", time: "19:30", duration_minutes: 75 }],
  },
  {
    id: "demo-school-class-la",
    name: "LA Salsa Partnerwork",
    description: "Cross-body technique, partner connection, and combinations.",
    style_tag: "LA Salsa",
    level_tag: "Intermediate",
    instructors: ["Tomas"],
    venue_id: "demo-school-venue-rooftop",
    schedules: [{ day: "THU", time: "20:00", duration_minutes: 75 }],
  },
  {
    id: "demo-school-class-friday",
    name: "Friday Salsa Lab",
    description: "Open-level coached practice followed by social dancing.",
    style_tag: "Salsa",
    level_tag: "Open Level",
    instructors: ["Lina", "Tomas"],
    venue_id: "demo-school-venue-main",
    schedules: [{ day: "FRI", time: "19:00", duration_minutes: 90 }],
  },
];

const DAY_TO_WEEKDAY = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };

function buildScheduleInstances() {
  const venueById = Object.fromEntries(DEMO_SCHOOL_VENUES.map((v) => [v.id, v]));
  return CLASS_SPECS.flatMap((cls) =>
    cls.schedules.map((sched, idx) => {
      const { hour, minute } = parseHm(sched.time);
      const datetime = nextWeekdayAt(DAY_TO_WEEKDAY[sched.day], hour, minute);
      const venue = venueById[cls.venue_id];
      return {
        id: `${cls.id}-inst-${idx}`,
        class_id: cls.id,
        class_name: cls.name,
        style_tag: cls.style_tag,
        level_tag: cls.level_tag,
        instructors: cls.instructors,
        venue_name: venue?.name || "Studio",
        datetime,
        day: sched.day,
        time: sched.time,
        duration_minutes: sched.duration_minutes,
        status: "SCHEDULED",
      };
    }),
  ).sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
}

export const DEMO_SCHOOL_CLASSES = CLASS_SPECS.map((c) => ({
  id: c.id,
  name: c.name,
  description: c.description,
  style_tag: c.style_tag,
  level_tag: c.level_tag,
  instructors: c.instructors,
  venue_id: c.venue_id,
  schedules: c.schedules,
}));

/** Tonight's live check-in — students in the check-in window. */
const FIXTURE_DOOR = [
  {
    id: "demo-door-carla",
    student_name: "Carla",
    role: "Follower",
    class_name: "Salsa Foundations",
    class_id: "demo-school-class-foundations",
    credits_remaining: 3,
    credit_impact: { will_debit_credits: true, credits_remaining_after: 2, via: "CREDIT" },
    status: "WAITING",
  },
  {
    id: "demo-door-marco",
    student_name: "Marco",
    role: "Leader",
    class_name: "Salsa Foundations",
    class_id: "demo-school-class-foundations",
    credits_remaining: 3,
    credit_impact: { will_debit_credits: true, credits_remaining_after: 2, via: "CREDIT" },
    status: "WAITING",
  },
  {
    id: "demo-door-yuki",
    student_name: "Yuki",
    role: "Both",
    class_name: "Salsa Foundations",
    class_id: "demo-school-class-foundations",
    credits_remaining: null,
    credit_impact: { will_debit_credits: false, via: "TIME_PASS" },
    status: "WAITING",
  },
];

/** Pending fee approvals — copy of real Action Items payments queue. */
const FIXTURE_FEES = [
  {
    id: "demo-fee-helena",
    guest_name: "Helena",
    product_name: "5-Class Pack",
    listed_price: 70,
    amount_paid: 70,
    currency: "EUR",
    method: "Bank transfer",
    note: "Paid this morning — please approve",
    status: "PENDING",
  },
  {
    id: "demo-fee-andre",
    guest_name: "Andre",
    product_name: "Single Class Drop-in",
    listed_price: 16,
    amount_paid: null,
    currency: "EUR",
    method: null,
    note: null,
    status: "PENDING",
  },
  {
    id: "demo-fee-vera",
    guest_name: "Vera",
    product_name: "5-Class Pack",
    listed_price: 70,
    amount_paid: 65,
    currency: "EUR",
    method: "Cash",
    note: "Said she paid 65",
    status: "PENDING",
  },
];

const FIXTURE_PEOPLE = [
  { id: "demo-person-carla", name: "Carla", role: "Follower", credits_remaining: 3, activity_label: "Student · 5-pack" },
  { id: "demo-person-yuki", name: "Yuki", role: "Both", credits_remaining: 3, activity_label: "Student · 5-pack" },
  { id: "demo-person-sofia", name: "Sofia", role: "Follower", credits_remaining: 3, activity_label: "Student · 5-pack" },
  { id: "demo-person-jamal", name: "Jamal", role: "Leader", credits_remaining: 3, activity_label: "Student · 5-pack" },
  { id: "demo-person-elena", name: "Elena", role: "Follower", credits_remaining: 3, activity_label: "Student · 5-pack" },
  { id: "demo-person-helena", name: "Helena", role: "Follower", credits_remaining: 0, activity_label: "Follower" },
  { id: "demo-person-andre", name: "Andre", role: "Leader", credits_remaining: 0, activity_label: "Follower" },
  { id: "demo-person-vera", name: "Vera", role: "Follower", credits_remaining: 0, activity_label: "Follower" },
];

function emptyState() {
  return {
    version: 1,
    door: FIXTURE_DOOR.map((d) => ({ ...d })),
    fees: FIXTURE_FEES.map((f) => ({ ...f })),
    check_in_times: {},
    schedule: buildScheduleInstances(),
  };
}

export function readSchoolDemoState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1) return emptyState();
    return {
      ...emptyState(),
      ...parsed,
      door: Array.isArray(parsed.door) ? parsed.door : FIXTURE_DOOR.map((d) => ({ ...d })),
      fees: Array.isArray(parsed.fees) ? parsed.fees : FIXTURE_FEES.map((f) => ({ ...f })),
      check_in_times: parsed.check_in_times || {},
      schedule: Array.isArray(parsed.schedule) && parsed.schedule.length
        ? parsed.schedule
        : buildScheduleInstances(),
    };
  } catch {
    return emptyState();
  }
}

export function writeSchoolDemoState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  return state;
}

export function activateSchoolDemoSession(fromQuery) {
  if (!fromQuery) return;
  try {
    document.cookie = "rsvp_demo=1; path=/; max-age=86400; SameSite=Lax";
  } catch {
    // ignore
  }
}

export function resetSchoolDemo({ redirectTo = SCHOOL_DEMO_PATH } = {}) {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  window.location.assign(redirectTo);
}

export function markSchoolDemoDoorAttendance(doorId, state = readSchoolDemoState()) {
  const now = new Date();
  const timeLabel = now.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const door = (state.door || []).map((item) =>
    item.id === doorId ? { ...item, status: "ATTENDED" } : item,
  );
  return writeSchoolDemoState({
    ...state,
    door,
    check_in_times: { ...(state.check_in_times || {}), [doorId]: timeLabel },
  });
}

export function markSchoolDemoDoorNoShow(doorId, state = readSchoolDemoState()) {
  const door = (state.door || []).map((item) =>
    item.id === doorId ? { ...item, status: "NO_SHOW" } : item,
  );
  return writeSchoolDemoState({ ...state, door });
}

export function reviewSchoolDemoFee(feeId, approve, state = readSchoolDemoState()) {
  const fees = (state.fees || []).map((fee) =>
    fee.id === feeId
      ? { ...fee, status: approve ? "APPROVED" : "REJECTED" }
      : fee,
  );
  return writeSchoolDemoState({ ...state, fees });
}

export function schoolDemoPendingCounts(state = readSchoolDemoState()) {
  const doorWaiting = (state.door || []).filter((d) => d.status === "WAITING").length;
  const feesPending = (state.fees || []).filter((f) => f.status === "PENDING").length;
  return {
    door: doorWaiting,
    fees: feesPending,
    total: doorWaiting + feesPending,
  };
}

export function schoolDemoPeople() {
  return FIXTURE_PEOPLE.map((p) => ({ ...p }));
}
